import React, { useCallback, useEffect, useState } from 'react';
import { FaHeadset, FaTimes, FaUserCheck } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import notify from '../services/notify';
import { PERFIS_AEREO } from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';

/**
 * Atendimento do Aéreo por evento (`EventoAereoAtendimentoService` no back).
 *
 * `useAtendimentoAereo(idEvento, aoMudar)` lê quem atende o evento e diz se o usuário opera a malha e
 * a emissão do evento (`opera`). O Aéreo e o Gerente Aéreo operam o evento que atendem. Sem atender (o
 * `motivo` do back), o modal abre assim que a tela carrega e oferece assumir — qualquer pessoa do Aéreo
 * assume a qualquer momento —, a tela fica só leitura, e o back recusa do mesmo jeito.
 * Fechado o modal, o atalho azul do cabeçalho reabre; ele fica para todo o Aéreo, que lê quem atende mesmo
 * sem atender. O atendimento é gestão interna do Aéreo: RSVP e Eventos não o veem. O Gerente Aéreo e o
 * admin tiram alguém do atendimento. O admin opera sem atender. Na emissão, cada linha tem ainda quem
 * atende o participante: é a tela que confere.
 *
 * `aoMudar` roda depois de assumir ou desvincular, para a tela reler o que o back libera.
 * Devolve `atalho` (vai na lista de atalhos do `CabecalhoEventoAereo`), `modal` (a tela renderiza) e
 * `abrir`, para quem abre o modal por fora — o painel de eventos, com o evento escolhido na linha.
 */
export function useAtendimentoAereo(idEvento, aoMudar) {
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem('perfil');
    // O atendimento é gestão interna do Aéreo: só ele e o admin leem quem atende.
    const doFluxo = PERFIS_AEREO.includes(perfil) || perfil === 'admin';

    const [estado, setEstado] = useState(null);
    const [aberto, setAberto] = useState(false);
    const [processando, setProcessando] = useState(false);

    useEffect(() => {
        // Trocou de evento (o painel de eventos reaproveita o hook): o estado do anterior sai, para o
        // modal não abrir com as pessoas de outro evento.
        setEstado(null);
        if (!idEvento || !doFluxo) return undefined;
        let vivo = true;
        api({ method: 'get', action: '/eventoAereoAtendimento', token, params: { evento: idEvento } })
            .then(res => {
                if (!vivo) return;
                const e = res.data.data ?? null;
                setEstado(e);
                if (e && e.motivo) setAberto(true);
            })
            .catch(err => notify.apiError(err, 'Erro ao ler quem atende o evento'));
        return () => { vivo = false; };
    }, [idEvento, doFluxo, token]);

    // `fechar`: o desvincular deixa o modal aberto, para tirar mais de uma pessoa sem reabrir.
    const gravar = useCallback((action, data, sucesso, fechar = true) => {
        setProcessando(true);
        api({ method: 'post', action, token, data: { evento: idEvento, ...data } })
            .then(res => {
                setEstado(res.data.data ?? null);
                if (fechar) setAberto(false);
                toast.success(sucesso);
                if (aoMudar) aoMudar();
            })
            .catch(err => notify.apiError(err, 'Erro ao gravar o atendimento'))
            .finally(() => setProcessando(false));
    }, [idEvento, token, aoMudar]);

    const assumir = () => gravar('/eventoAereoAtendimento/assumir', {}, 'Você assumiu este evento');
    const desvincular = (pessoa) => gravar('/eventoAereoAtendimento/desvincular', { participante: pessoa.id },
        `${pessoa.nome} deixou de atender este evento`, false);

    // Enquanto o estado não chega, o Aéreo não opera: o botão não aparece liberado antes da resposta.
    // Quem diz se falta vínculo é o back (`motivo`): o usuário atendendo o evento.
    const opera = !PERFIS_AEREO.includes(perfil) || (!!estado && !estado.motivo);
    const bloqueado = !!estado?.motivo;

    // O atalho aparece para todo o Aéreo, atendendo ou não, e para o admin. Assumir, para quem ainda não
    // atende o evento; para quem já atende, o modal mostra quem mais atende.
    const atalho = estado ? {
        rotulo: estado.podeAssumir ? 'Assumir atendimento' : 'Atendimento',
        titulo: bloqueado ? estado.motivo : 'Quem atende este evento no Aéreo.',
        onClick: () => setAberto(true),
        destaque: true,
        icone: <FaHeadset size={11} />,
    } : null;

    const modal = aberto && estado ? (
        <AtendimentoAereoModal
            estado={estado} processando={processando} onAssumir={assumir} onDesvincular={desvincular}
            onFechar={() => { if (!processando) setAberto(false); }}
        />
    ) : null;

    // Abre por fora: o modal aparece quando o estado do evento chegar.
    const abrir = useCallback(() => setAberto(true), []);

    return { estado, opera, atalho, modal, abrir };
}

// O "x" dentro do nome, no modal: tirar do atendimento.
function BotaoTirar({ titulo, onClick, disabled }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} title={titulo} aria-label={titulo}
            style={{ background: 'none', border: 0, padding: 0, marginLeft: 4, color: 'inherit', lineHeight: 1, cursor: 'pointer' }}>
            <FaTimes size={10} />
        </button>
    );
}

function AtendimentoAereoModal({ estado, processando, onAssumir, onDesvincular, onFechar }) {
    // Faltando vínculo, o `motivo` do back diz o que fazer (assumir). Sem faltar, quem atende.
    const subtitulo = estado.motivo
        ? `${estado.motivo} Sem isso, as telas ficam só para leitura.`
        : 'Quem do Aéreo atende este evento.';

    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg"
            onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaHeadset size={17} /></span>
                        <h3 className="fin-pp__modal-title">Atendimento do evento
                            <span className="fin-pp__modal-sub">{subtitulo}</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar} disabled={processando}>
                            <FaTimes size={16} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="fin-pp__label" style={{ marginBottom: 8 }}>Atendendo agora</div>
                        {estado.atendentes.length === 0 ? (
                            <p className="fin-pp__cell-muted" style={{ margin: 0 }}>Ninguém atende este evento ainda.</p>
                        ) : (
                            <div className="d-flex flex-wrap gap-2">
                                {estado.atendentes.map(p => (
                                    <span key={p.id} className="fin-pp__pill is-info">
                                        {p.nome}{p.gerente ? ' · Gerente Aéreo' : ''}
                                        {/* Tirar do atendimento é só do Gerente Aéreo (e do admin). */}
                                        {estado.podeRevogar && (
                                            <BotaoTirar onClick={() => onDesvincular(p)} disabled={processando}
                                                titulo={`Tirar ${p.nome} do atendimento deste evento`} />
                                        )}
                                    </span>
                                ))}
                            </div>
                        )}
                        <p className="fin-pp__cell-muted" style={{ margin: '16px 0 0' }}>
                            Qualquer pessoa do Aéreo assume o evento a qualquer momento, e o Aéreo recebe um e-mail. O Gerente Aéreo tira alguém do atendimento.
                        </p>
                    </div>
                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={onFechar} disabled={processando}>Fechar</button>
                        {estado.podeAssumir && (
                            <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                onClick={onAssumir} disabled={processando}>
                                <FaUserCheck size={13} /> Assumir o atendimento
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
